import type { DocMigration } from "../docMigration";

const defaultDatumIds = [
  "base_wall_bottom",
  "base_wall_top",
  "gable_end_bottom",
  "gable_end_top",
  "wall_start",
  "wall_end",
] as const;

function isDefault(d: Datum): string | false {
  return defaultDatumIds.find((x) => d.name.match(`: ${x}$`)) || false;
}

interface Datum {
  name: string;
  offset: number;
  datumType: string;
  offsetNormalized: boolean;
}

function datumsEq(a: Datum, b: Datum): boolean {
  return a.offset === b.offset && a.datumType === b.datumType && a.offsetNormalized === b.offsetNormalized;
}

const facadeSpanStratumDatumKeys = ["endDatum", "startDatum", "upperDatum", "lowerDatum"];

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_286: DocMigration = {
  maxSchemaVersion: "0.0.286",
  description: "Merge duplicate default datums",
  apply(json: any): any {
    if (!json.settings) {
      return json;
    }

    interface DatumCatalog {
      datums: { [key: string]: Datum };
    }

    const datumCatalogs: DatumCatalog[] = [];

    for (const settingId in json.settings) {
      const setting = json.settings[settingId];
      if (setting.type === "DATUM_CATALOG_SETTINGS") {
        datumCatalogs.push(setting);
        break;
      }
    }

    if (datumCatalogs.length === 0) {
      return json;
    }

    const dedupeMap: { [key: string]: string } = {};
    const catalogMap: { [key: string]: DatumCatalog } = {};
    for (const datumCatalog of datumCatalogs) {
      for (const [id, datum] of Object.entries(datumCatalog?.datums ?? {})) {
        const isDefaultName = isDefault(datum);

        if (isDefaultName && datumsEq(datumCatalog.datums[isDefaultName], datum)) {
          dedupeMap[id] = isDefaultName;
          catalogMap[id] = datumCatalog;
        }
      }
    }

    for (const id of Object.keys(dedupeMap)) {
      const { datums } = catalogMap[id];
      delete datums[id];
    }

    if (json.facadeSpanStrata) {
      for (const facadeStratum of Object.values<{ datum: string }>(json.facadeSpanStrata)) {
        for (const datumKey of facadeSpanStratumDatumKeys) {
          if (dedupeMap[facadeStratum[datumKey]]) {
            facadeStratum[datumKey] = dedupeMap[facadeStratum[datumKey]];
          }
        }
      }
    }

    if (json.facadeSweepStrata) {
      for (const facadeSweep of Object.values<{ datum: string }>(json.facadeSweepStrata)) {
        if (dedupeMap[facadeSweep["datum"]]) {
          facadeSweep["datum"] = dedupeMap[facadeSweep["datum"]];
        }
      }
    }

    return json;
  },
};

export default migration_0_0_286;
